import { useState, useCallback } from 'react';
import {
  Text,
  Code,
  Icon,
  InlineStack,
  FormattedDateTime,
  Spinner,
  Flex,
  Divider,
} from '@customerio/pluma-components/react';
import { useQuery } from '@tanstack/react-query';
import type { SearchResult } from '@/services/auth/types';
import { useServicesQuery, servicesApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { TEMPLATE_TYPE_ICONS, TEMPLATE_TYPE_LABELS } from '../constants';
import { MetadataRow } from './metadata-row';
import * as styles from './index.css';

type Props = { result: Extract<SearchResult, { kind: 'template' }> };

const PREVIEWABLE_TEMPLATE_TYPES = new Set(['email']);

export function TemplatePreview({ result }: Props) {
  const envId = Number(result.other.env_id);
  const templateId = Number(result.other.id);
  const isPreviewable = PREVIEWABLE_TEMPLATE_TYPES.has(result.other.template_type);
  const templateIcon = TEMPLATE_TYPE_ICONS[result.other.template_type];
  const templateLabel = TEMPLATE_TYPE_LABELS[result.other.template_type];

  const {
    data: templateData,
    isLoading: isTemplateLoading,
    isError: isTemplateError,
  } = useServicesQuery(
    'getTemplateById',
    { params: { environment_id: envId, template_id: templateId } },
    { enabled: isPreviewable && envId > 0 && templateId > 0, staleTime: 5 * 60 * 1000, forwardAbortSignal: true },
  );

  const template = templateData?.template;
  const hasBody = template != null && !isEmptyString(template.body);

  const {
    data: previewData,
    isPending: isPreviewPending,
    isError: isPreviewError,
  } = useQuery({
    queryKey: ['templatePreview', envId, templateId, result.other.updated_at],
    // TODO(ui-api): spec gap (item 10) — previews.email request body reuses response model
    queryFn: ({ signal }) =>
      servicesApi.previewEmail({
        params: { environment_id: envId },
        body: {
          body: template!.body,
          subject: template!.subject ?? '',
          layout: '{{content}}',
          preprocessor: template!.preprocessor ?? 'premailer',
          headers: [],
          variables: {},
        },
        fetchOptions: { signal },
      }),
    enabled: hasBody,
    staleTime: 5 * 60 * 1000,
  });

  const renderedHtml = previewData?.body;
  const isLoading = isTemplateLoading || (hasBody && isPreviewPending);
  const isError = isTemplateError || isPreviewError;

  const [scale, setScale] = useState(0);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node == null) {
      setScale(0);
      return;
    }
    const observer = new ResizeObserver(() => {
      setScale(node.clientWidth / styles.IFRAME_WIDTH);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {isPreviewable ? (
        <>
          {isLoading ? (
            <Flex alignItems="center" justifyContent="center" py="200">
              <Spinner />
            </Flex>
          ) : isError ? (
            <Text size="xs" color="subtle">
              Failed to load preview
            </Text>
          ) : !isEmptyString(renderedHtml) ? (
            <div ref={containerRef} className={styles.templatePreviewContainer}>
              {scale > 0 ? (
                <div
                  style={{
                    width: styles.IFRAME_WIDTH * scale,
                    height: styles.IFRAME_HEIGHT * scale,
                    overflow: 'hidden',
                  }}
                >
                  <iframe
                    title="Template preview"
                    srcDoc={renderedHtml}
                    className={styles.templatePreviewIframe}
                    sandbox=""
                    style={{ transform: `scale(${scale})` }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {isLoading || isError || !isEmptyString(renderedHtml) ? <Divider mt="100" /> : null}
        </>
      ) : null}

      <Flex direction="column" gap="100" mt={isPreviewable ? '100' : undefined}>
        <MetadataRow label="ID">
          <Code size="xs">{result.other.id}</Code>
        </MetadataRow>
        <MetadataRow label="Type">
          <InlineStack gap="050" alignBlock="center">
            <Icon name={templateIcon ?? 'email'} size="xs" sizeVersion="v2" color="subtle" />
            <Text size="xs">{templateLabel ?? 'Template'}</Text>
          </InlineStack>
        </MetadataRow>
        {result.other.subject != null ? (
          <MetadataRow label="Subject">
            <Text size="xs" className="fs-mask">
              {result.other.subject}
            </Text>
          </MetadataRow>
        ) : null}
        {result.other.language != null ? (
          <MetadataRow label="Language">
            <Text size="xs">{result.other.language}</Text>
          </MetadataRow>
        ) : null}
        <MetadataRow label="Created">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.created_at} format="medium" />
          </Text>
        </MetadataRow>
        <MetadataRow label="Updated">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.updated_at} format="medium" />
          </Text>
        </MetadataRow>
      </Flex>
    </>
  );
}
